/**
 * permissionStore.js
 * Comprehensive permission management system for the Motorclub Service Provider Portal
 */

// Constants for permission types
export const PERMISSION_TYPES = {
  PAGE_ACCESS: 'pageAccess',
  JOB_ACTIONS: 'jobActions',
  USER_MANAGEMENT: 'userManagement',
  JOB_CREATION: 'jobCreation',
  JOB_VIEWING: 'jobViewing',
  JOB_EDITING: 'jobEditing',
  JOB_DISPATCHING: 'jobDispatching',
  GOA_APPROVAL: 'goaApproval',
  UNSUCCESSFUL_APPROVAL: 'unsuccessfulApproval',
  GOA_REQUEST: 'goaRequest',
  UNSUCCESSFUL_REQUEST: 'unsuccessfulRequest',
};

// Constants for primary roles
export const PRIMARY_ROLES = {
  OWNER: 'OW',
  SUB_OWNER: 'sOW',
  PARTNER_OWNER: 'OWp', // the owner of a partner company: an OW inside their own company
  REGIONAL_MANAGER: 'RM',
  SERVICE_PROVIDER: 'SP',
  UNDEFINED: 'N/A',
};

// Every permission rule below is written for OW; a partner owner is an OW inside
// their company, so the role is normalized once when permissions are processed
// and the real role is kept as `actualPrimaryRole` for display.
export const effectivePrimaryRole = (role) => (role === PRIMARY_ROLES.PARTNER_OWNER ? PRIMARY_ROLES.OWNER : role);

// Constants for secondary roles
export const SECONDARY_ROLES = {
  ADMIN: 'admin',
  DISPATCHER: 'dispatcher',
  ANSWERING_SERVICE: 'answeringService',
  DRIVER: 'driver',
};

// Management roles always carry the driver secondary role; the server enforces
// this on every save, so the UI shows it as on and locked.
export const MANAGEMENT_PRIMARY_ROLES = ['OW', 'sOW', 'OWp', 'RM', 'SP'];
export const isManagementPrimaryRole = (role) => MANAGEMENT_PRIMARY_ROLES.includes(role);

// Constants for pages
export const PAGES = {
  DASHBOARD: 'Dashboard',
  SETTINGS: 'Settings',
  USERS: 'Users',
  PERFORMANCE: 'Performance',
  SUBMISSIONS: 'Submissions',
  REGIONS: 'Regions',
  PAYMENTS: 'Payments',
};

// Constants for job status sequence (forward progression only for drivers)
export const JOB_STATUS_SEQUENCE = [
  'Pending',
  'Scheduled',
  'Pending Acceptance',
  'Dispatched',
  'En Route',
  'On Site',
  'Completed',
  'GOA',
  'Unsuccessful',
  'Expired',
];

// Local storage key for permissions
const PERMISSIONS_STORAGE_KEY = 'wk_user_permissions';

// --- Core Storage Functions ---
export function getStoredPermissions() {
  try {
    const permissionsData = localStorage.getItem(PERMISSIONS_STORAGE_KEY);
    return permissionsData ? JSON.parse(permissionsData) : null;
  } catch (error) {
    // console.error('Error retrieving stored permissions:', error); // Removed log
    return null;
  }
}

export function storePermissions(permissionsData) {
  if (!permissionsData) {
    // console.error('Cannot store permissions: No data provided'); // Removed log
    return;
  }
  try {
    localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(permissionsData));
    //  // Reduce console noise - Keeping this commented line as it was already commented
  } catch (error) {
    // console.error('Error storing permissions:', error); // Removed log
  }
}

export function clearPermissions() {
  try {
    localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
    //  // Removed log
  } catch (error) {
    // console.error('Error clearing permissions:', error); // Removed log
  }
}

// --- Internal Helper Functions ---
export function isDriverOnly(primaryRole, secondaryRoles) {
  const roles = Array.isArray(secondaryRoles) ? secondaryRoles : (secondaryRoles ? Object.keys(secondaryRoles).filter(k => secondaryRoles[k]) : []);
  return primaryRole === PRIMARY_ROLES.UNDEFINED &&
         roles.length === 1 &&
         roles.includes(SECONDARY_ROLES.DRIVER);
}


function calculatePageAccess(primaryRole, secondaryRoles, vendorId) {
  const roles = Array.isArray(secondaryRoles) ? secondaryRoles : (secondaryRoles ? Object.keys(secondaryRoles).filter(k => secondaryRoles[k]) : []);
  
  // Debug log to see what's being passed to calculatePageAccess
  
  
  const access = {
    [PAGES.DASHBOARD]: true, [PAGES.SETTINGS]: false, [PAGES.USERS]: false,
    [PAGES.PERFORMANCE]: false, [PAGES.SUBMISSIONS]: false, [PAGES.REGIONS]: false,
    [PAGES.PAYMENTS]: false,
  };
  access[PAGES.DASHBOARD.toLowerCase()] = true; // Ensure lowercase dashboard access

  if ([PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER].includes(primaryRole)) {
    Object.keys(access).forEach(page => access[page] = true);
  } else if (primaryRole === PRIMARY_ROLES.REGIONAL_MANAGER) {
    access[PAGES.SETTINGS] = true; access[PAGES.USERS] = true;
    access[PAGES.PERFORMANCE] = true; access[PAGES.PAYMENTS] = true;
    access[PAGES.SUBMISSIONS] = true; // Submissions and Regions belong to owners and regional managers
    access[PAGES.REGIONS] = true;
  } else if (primaryRole === PRIMARY_ROLES.SERVICE_PROVIDER) {
    access[PAGES.SETTINGS] = true; access[PAGES.USERS] = true;
    access[PAGES.PERFORMANCE] = true; access[PAGES.PAYMENTS] = true;
  }

  if (roles.includes(SECONDARY_ROLES.ADMIN)) {
     if (primaryRole === PRIMARY_ROLES.UNDEFINED) {
       access[PAGES.SETTINGS] = true; access[PAGES.USERS] = true;
    }
  }
  if (roles.includes(SECONDARY_ROLES.DISPATCHER) || roles.includes(SECONDARY_ROLES.ANSWERING_SERVICE)) {
    access[PAGES.PERFORMANCE] = true;
  }
  
  // Grant Performance page access to Org 1 driver-only users only
  if (primaryRole === PRIMARY_ROLES.UNDEFINED &&
      roles.length === 1 &&
      roles.includes(SECONDARY_ROLES.DRIVER)) {
    
    // Check if the user is from Org 1 before granting access
    // This is determined by the vendorId - Org 1 users have vendorId = '1' or starting with 'OW'
    const vendorId = arguments[2]; // Get vendorId from the third argument
    if (vendorId === '1' || (vendorId && vendorId.startsWith('OW'))) {
      access[PAGES.PERFORMANCE] = true;
    }
  }
  
  // Debug log to see what access rights are being granted
  
  
  return access;
}

function canCreateJobs(primaryRole, secondaryRoles, vendorId) {
  const roles = Array.isArray(secondaryRoles) ? secondaryRoles : (secondaryRoles ? Object.keys(secondaryRoles).filter(k => secondaryRoles[k]) : []);
  if ([PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole)) return true;
  if (primaryRole === PRIMARY_ROLES.SERVICE_PROVIDER) return false;
  if (primaryRole === PRIMARY_ROLES.UNDEFINED && (roles.includes(SECONDARY_ROLES.DISPATCHER) || roles.includes(SECONDARY_ROLES.ANSWERING_SERVICE))) {
    return vendorId === '1' || (vendorId && vendorId.startsWith('OW'));
  }
  return false;
}

function _canDispatchJobsFlag(primaryRole, secondaryRoles, vendorId) {
  const roles = Array.isArray(secondaryRoles) ? secondaryRoles : (secondaryRoles ? Object.keys(secondaryRoles).filter(k => secondaryRoles[k]) : []);
  if ([PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER, PRIMARY_ROLES.SERVICE_PROVIDER].includes(primaryRole)) return true;
  if (primaryRole === PRIMARY_ROLES.UNDEFINED && (roles.includes(SECONDARY_ROLES.DISPATCHER) || roles.includes(SECONDARY_ROLES.ANSWERING_SERVICE))) return true;
  return false;
}

// White Knight management decides GOA requests; a provider's owner, admins and
// dispatchers decide their own driver's request first (the server enforces
// which stage each may act on)
function canApproveGOA(primaryRole, secondaryRoles) {
  const roles = Array.isArray(secondaryRoles) ? secondaryRoles : (secondaryRoles ? Object.keys(secondaryRoles).filter(k => secondaryRoles[k]) : []);
  if ([PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER, PRIMARY_ROLES.SERVICE_PROVIDER].includes(primaryRole)) return true;
  if (primaryRole === PRIMARY_ROLES.UNDEFINED && (roles.includes(SECONDARY_ROLES.ADMIN) || roles.includes(SECONDARY_ROLES.DISPATCHER))) return true;
  return false;
}

function canRequestGOA(primaryRole, secondaryRoles) {
  const roles = Array.isArray(secondaryRoles) ? secondaryRoles : (secondaryRoles ? Object.keys(secondaryRoles).filter(k => secondaryRoles[k]) : []);
  if ([PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER, PRIMARY_ROLES.SERVICE_PROVIDER].includes(primaryRole)) return true;
  if (primaryRole === PRIMARY_ROLES.UNDEFINED && (roles.includes(SECONDARY_ROLES.DISPATCHER) || roles.includes(SECONDARY_ROLES.ANSWERING_SERVICE) || roles.includes(SECONDARY_ROLES.ADMIN))) return true;
  return false;
}

function canApproveUnsuccessful(primaryRole, secondaryRoles) {
  return canApproveGOA(primaryRole, secondaryRoles);
}

function canRequestUnsuccessful(primaryRole, secondaryRoles) {
  return canRequestGOA(primaryRole, secondaryRoles);
}

function calculateAllowedJobProgressions(primaryRole, secondaryRoles) {
  const progressions = {};
  const roles = Array.isArray(secondaryRoles) ? secondaryRoles : (secondaryRoles ? Object.keys(secondaryRoles).filter(k => secondaryRoles[k]) : []);
  const isDriverRestricted = isDriverOnly(primaryRole, roles) ||
                             (primaryRole === PRIMARY_ROLES.UNDEFINED &&
                              !roles.includes(SECONDARY_ROLES.ADMIN) &&
                              !roles.includes(SECONDARY_ROLES.DISPATCHER) &&
                              !roles.includes(SECONDARY_ROLES.ANSWERING_SERVICE));
  if (isDriverRestricted) {
    progressions['Dispatched'] = ['En Route'];
    progressions['En Route'] = ['On Site'];
    progressions['On Site'] = ['Completed'];
  } else {
    JOB_STATUS_SEQUENCE.forEach(currentStatus => {
      progressions[currentStatus] = [...JOB_STATUS_SEQUENCE];
    });
  }
  return progressions;
}

const roleHierarchy = {
  [PRIMARY_ROLES.OWNER]: 100, [PRIMARY_ROLES.SUB_OWNER]: 90, [PRIMARY_ROLES.REGIONAL_MANAGER]: 80,
  [PRIMARY_ROLES.SERVICE_PROVIDER]: 70, [SECONDARY_ROLES.ADMIN]: 60, [SECONDARY_ROLES.DISPATCHER]: 50,
  [SECONDARY_ROLES.ANSWERING_SERVICE]: 40, [SECONDARY_ROLES.DRIVER]: 30, [PRIMARY_ROLES.UNDEFINED]: 0,
};

// Frontend equivalent of backend getUserPrivilegeLevel
const getUserPrivilegeLevel = (user) => {
  if (!user) return 0;
  const primaryRole = user.primaryRole || user.role; // Handle legacy 'role'
  let level = roleHierarchy[primaryRole] || 0;

  // Adjust for secondary roles if primary is N/A or SP
  if (primaryRole === PRIMARY_ROLES.SERVICE_PROVIDER && user.secondaryRoles?.admin) {
      // SP Admin level is same as SP for hierarchy checks
  } else if (primaryRole === PRIMARY_ROLES.UNDEFINED) {
      if (user.secondaryRoles?.admin) level = Math.max(level, 60);
      if (user.secondaryRoles?.dispatcher) level = Math.max(level, 50);
      if (user.secondaryRoles?.answeringService) level = Math.max(level, 40);
      if (user.secondaryRoles?.driver) level = Math.max(level, 30);
  }
  return level;
};

// Frontend equivalent of backend getRoleCategory
export const getRoleCategory = (user) => {
   if (!user) return null;
   const primaryRole = user.primaryRole || user.role;
   if (primaryRole === PRIMARY_ROLES.OWNER || primaryRole === PRIMARY_ROLES.SUB_OWNER) return 'OW';
   if (primaryRole === PRIMARY_ROLES.REGIONAL_MANAGER) return 'RM';
   if (primaryRole === PRIMARY_ROLES.SERVICE_PROVIDER) return 'SP';
   return null;
}

/**
 * Determines the organization level of a user based on their role.
 * Organization Level 1: OW, sOW, RM, or N/A with admin/dispatcher secondary roles
 * Organization Level 2: SP or any other user
 * @param {object} user - The user object
 * @returns {number} - 1 for Org Level 1, 2 for Org Level 2
 */
const getUserOrganizationLevel = (user) => {
  if (!user) return 2; // Default to Org Level 2 if no user provided

  // --- CORRECTED LOGIC ---
  // 1. Prioritize the explicit organizationLevel field if it exists
  if (user.organizationLevel === 1 || user.organizationLevel === 2) {
    return user.organizationLevel;
  }

  // --- FALLBACK LOGIC (for older data potentially missing the field) ---
  // console.warn('[getUserOrganizationLevel] Fallback: User object missing organizationLevel field. Inferring from roles.'); // Optional warning
  const primaryRole = user.primaryRole || user.role;

  // Org Level 1: OW, sOW, RM
  if (primaryRole === PRIMARY_ROLES.OWNER || primaryRole === PRIMARY_ROLES.SUB_OWNER || primaryRole === PRIMARY_ROLES.REGIONAL_MANAGER) {
    return 1;
  }

  // Org Level 1 (Fallback): N/A with admin or dispatcher secondary roles
  // This fallback might still be incorrect based on business rules, but kept for potential compatibility
  const hasAdmin = user.secondaryRoles?.admin === true;
  const hasDispatcher = user.secondaryRoles?.dispatcher === true;
  if (primaryRole === PRIMARY_ROLES.UNDEFINED && (hasAdmin || hasDispatcher)) {
    return 1;
  }

  // All others default to Org Level 2
  return 2;
};


// --- Exported Permission Functions ---

export function processPermissions(userData) {
  if (!userData) { /* console.error('Cannot process permissions: No user data provided'); */ return { isAuthenticated: false, permissions: {}, userData: null }; } // Removed log
  try {
    const actualPrimaryRole = userData.primaryRole || PRIMARY_ROLES.UNDEFINED;
    const primaryRole = effectivePrimaryRole(actualPrimaryRole);
    let secondaryRolesArray = [];
    if (userData.secondaryRoles) {
      if (Array.isArray(userData.secondaryRoles)) {
        secondaryRolesArray = userData.secondaryRoles;
      } else if (typeof userData.secondaryRoles === 'object') {
        secondaryRolesArray = Object.keys(userData.secondaryRoles).filter(role => userData.secondaryRoles[role] === true);
      }
    }
    const secondaryRolesObject = (typeof userData.secondaryRoles === 'object' && !Array.isArray(userData.secondaryRoles))
                                 ? userData.secondaryRoles
                                 : secondaryRolesArray.reduce((acc, role) => { acc[role] = true; return acc; }, {});
   const vendorId = userData.vendorId || userData.vendorNumber || '';
   const regions = Array.isArray(userData.regions) ? userData.regions.map(r => r._id || r) : (userData.region ? [userData.region._id || userData.region] : []); // Extract IDs if objects
   const isDriverOnlyUser = isDriverOnly(primaryRole, secondaryRolesObject); // Use object for consistency
   const organizationLevel = userData.organizationLevel; // Get organizationLevel directly

   const permissions = {
     userId: userData._id || userData.id || '', username: userData.username || '', email: userData.email || '',
      // The company this login belongs to (from the login / me payload)
      actualPrimaryRole,
      orgId: userData.orgId || '', orgSlug: userData.orgSlug || '', orgKind: userData.orgKind || 'platform',
      orgLabel: userData.orgLabel || '', organization: userData.organization || null,
      fullName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(), primaryRole,
      secondaryRoles: secondaryRolesObject, _secondaryRolesArray: secondaryRolesArray, vendorId, regions,
      pageAccess: calculatePageAccess(primaryRole, secondaryRolesArray, vendorId),
      canCreateJobs: canCreateJobs(primaryRole, secondaryRolesArray, vendorId),
      canDispatchJobs: _canDispatchJobsFlag(primaryRole, secondaryRolesArray, vendorId),
      canApproveGOA: canApproveGOA(primaryRole, secondaryRolesArray),
      canRequestGOA: canRequestGOA(primaryRole, secondaryRolesArray),
      canApproveUnsuccessful: canApproveUnsuccessful(primaryRole, secondaryRolesArray),
      canRequestUnsuccessful: canRequestUnsuccessful(primaryRole, secondaryRolesArray),
      isDriverOnly: isDriverOnlyUser,
      canCreateUsers: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) ||
                     (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)) ||
                     (organizationLevel === 2 && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)),
      canFilterUsers: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole),
      canEditUserRoles: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.SERVICE_PROVIDER) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)), // Added SP
      canEditUserStatus: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER, PRIMARY_ROLES.SERVICE_PROVIDER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)), // Added SP
      canEditUserDutyStatus: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) || secondaryRolesArray.includes(SECONDARY_ROLES.DISPATCHER) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)),
      canDeleteUsers: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)), // Deactivation permission
      canEditUsers: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER, PRIMARY_ROLES.SERVICE_PROVIDER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)), // General edit permission
      canManageUser2FA: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)),
      canEditRates: !isDriverOnlyUser && primaryRole !== PRIMARY_ROLES.SERVICE_PROVIDER,
      canUseRateFilters: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)),
      canManageSites: !isDriverOnlyUser && primaryRole !== PRIMARY_ROLES.SERVICE_PROVIDER,
      canUseSiteFilters: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)),
      canChangeSiteStatus: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)),
      canCreateSites: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)),
      canManageFleet: !isDriverOnlyUser && primaryRole !== PRIMARY_ROLES.SERVICE_PROVIDER,
      canUseFleetFilters: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)),
      markJobsGoa: !isDriverOnlyUser, markJobsUnsuccessful: !isDriverOnlyUser, duplicateJobs: !isDriverOnlyUser,
      reactivateJobs: !isDriverOnlyUser, updateJobsInCompletedTabs: !isDriverOnlyUser, updateJobsInCanceledTabs: !isDriverOnlyUser,
      deleteJobs: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) || (primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.DISPATCHER) && (vendorId === '1' || (vendorId && vendorId.startsWith('OW')))),
      cancelJobs: !isDriverOnlyUser,
      // Billing notes (invoiceNotes) are restricted to admins of either org
      // (SP company owners count as their org's admin). Dispatchers, drivers
      // and all other roles never see them.
      canSeeBillingNotes: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER, PRIMARY_ROLES.SERVICE_PROVIDER].includes(primaryRole) ||
                          secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN),
      allowedJobProgressions: calculateAllowedJobProgressions(primaryRole, secondaryRolesObject), // Use object
      
      // Background Checks permissions - Only for Org 1 users (OW, sOW, RM, or N/A with admin/dispatcher from Org 1)
      canViewBackgroundChecks: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) ||
                              ((primaryRole === PRIMARY_ROLES.UNDEFINED && (secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN) || secondaryRolesArray.includes(SECONDARY_ROLES.DISPATCHER))) &&
                               (vendorId === '1' || (vendorId && vendorId.startsWith('OW')))),
      canEditBackgroundChecks: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) ||
                              ((primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)) &&
                               (vendorId === '1' || (vendorId && vendorId.startsWith('OW')))),
      canCreateBackgroundChecks: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) ||
                                ((primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)) &&
                                 (vendorId === '1' || (vendorId && vendorId.startsWith('OW')))),
      canRequestBackgroundChecks: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) ||
                                 ((primaryRole === PRIMARY_ROLES.UNDEFINED && (secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN) || secondaryRolesArray.includes(SECONDARY_ROLES.DISPATCHER))) &&
                                  (vendorId === '1' || (vendorId && vendorId.startsWith('OW')))),
      canDeleteBackgroundChecks: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) ||
                                ((primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)) &&
                                 (vendorId === '1' || (vendorId && vendorId.startsWith('OW')))),
      
      // Insurance permissions - Only for Org 1 users (OW, sOW, RM, or N/A with admin/dispatcher from Org 1)
      canViewInsurance: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) ||
                       ((primaryRole === PRIMARY_ROLES.UNDEFINED && (secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN) || secondaryRolesArray.includes(SECONDARY_ROLES.DISPATCHER))) &&
                        (vendorId === '1' || (vendorId && vendorId.startsWith('OW')))),
      canEditInsurance: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) ||
                       ((primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)) &&
                        (vendorId === '1' || (vendorId && vendorId.startsWith('OW')))),
      canDeleteInsurance: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) ||
                         ((primaryRole === PRIMARY_ROLES.UNDEFINED && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)) &&
                          (vendorId === '1' || (vendorId && vendorId.startsWith('OW')))),
      // New permission for managing insurance - preserves original high-level role access + new org level + admin requirement
      canManageInsurance: [PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER, PRIMARY_ROLES.REGIONAL_MANAGER].includes(primaryRole) ||
                         (organizationLevel === 1 && secondaryRolesArray.includes(SECONDARY_ROLES.ADMIN)),
    };
    // Debug log to see what permissions are being calculated
    
    
    return { isAuthenticated: true, permissions, userData };
  } catch (error) {
    console.error('Error processing permissions:', error);
    return { isAuthenticated: false, permissions: {}, userData: null };
  }
}

// Frontend equivalent of backend canManageUser
export function canManageTargetUser(currentUserPermissions, targetUser) {
  if (!currentUserPermissions || !targetUser) { /* console.warn("canManageTargetUser (FE): Missing args"); */ return false; } // Removed log
  const requestingUser = {
      _id: currentUserPermissions.userId, primaryRole: currentUserPermissions.primaryRole,
      secondaryRoles: currentUserPermissions.secondaryRoles, vendorNumber: currentUserPermissions.vendorId,
      vendorId: currentUserPermissions.vendorId, regions: currentUserPermissions.regions || [],
  };
  const requesterId = requestingUser._id;
  const targetId = targetUser._id ? targetUser._id.toString() : targetUser.id;
  if (requesterId === targetId) return false;

  const requesterLevel = getUserPrivilegeLevel(requestingUser);
  const targetLevel = getUserPrivilegeLevel(targetUser);
  const requesterVendorId = requestingUser.vendorNumber || requestingUser.vendorId;
  const targetVendorId = targetUser.vendorNumber || targetUser.vendorId;
  const requesterPrimaryRole = requestingUser.primaryRole;
  const targetPrimaryRole = targetUser.primaryRole || targetUser.role;
  const isRequesterSPAdmin = requestingUser.secondaryRoles?.admin && requesterPrimaryRole === PRIMARY_ROLES.SERVICE_PROVIDER;
  const isRequesterNAAdmin = requestingUser.secondaryRoles?.admin && requesterPrimaryRole === PRIMARY_ROLES.UNDEFINED;

  if (requesterLevel < targetLevel) return false;
  if (requesterLevel === targetLevel) {
      if (requesterPrimaryRole === PRIMARY_ROLES.OWNER) { /* Allow OW */ }
      else if (requesterPrimaryRole === PRIMARY_ROLES.SUB_OWNER) return false;
      else if (requesterPrimaryRole === PRIMARY_ROLES.REGIONAL_MANAGER) return false;
      else if (requesterPrimaryRole === PRIMARY_ROLES.SERVICE_PROVIDER) {
          const isTargetSPAdmin = targetUser.secondaryRoles?.admin && targetPrimaryRole === PRIMARY_ROLES.SERVICE_PROVIDER;
          if (!isRequesterSPAdmin || isTargetSPAdmin) return false;
      } else if (requesterPrimaryRole === PRIMARY_ROLES.UNDEFINED) {
           if (!isRequesterNAAdmin) return false;
      } else { return false; }
  }

  switch (requesterPrimaryRole) {
      case PRIMARY_ROLES.OWNER: case PRIMARY_ROLES.SUB_OWNER: break;
      case PRIMARY_ROLES.REGIONAL_MANAGER:
          if (targetPrimaryRole === PRIMARY_ROLES.OWNER || targetPrimaryRole === PRIMARY_ROLES.SUB_OWNER) return false;
          const targetRegions = targetUser.regions?.map(r => r._id || r.toString()) || []; // Handle object or string IDs
          const requesterRegions = requestingUser.regions || []; // Already IDs
           if (!Array.isArray(targetRegions) || !Array.isArray(requesterRegions) || requesterRegions.length === 0) { /* console.warn(`Region check failed (FE)`); */ return false; } // Removed log
          if (!targetRegions.some(tr => requesterRegions.includes(tr))) return false;
          break;
      case PRIMARY_ROLES.SERVICE_PROVIDER:
           if (targetPrimaryRole === PRIMARY_ROLES.OWNER || targetPrimaryRole === PRIMARY_ROLES.SUB_OWNER || targetPrimaryRole === PRIMARY_ROLES.REGIONAL_MANAGER) return false;
           if (!requesterVendorId || !targetVendorId || requesterVendorId !== targetVendorId) return false;
           break;
      default: // N/A + secondary roles
           if (isRequesterNAAdmin) {
               if (targetPrimaryRole !== PRIMARY_ROLES.UNDEFINED) return false;
               if (!requesterVendorId || !targetVendorId || requesterVendorId !== targetVendorId) return false;
           } else { return false; }
           break;
  }
  return true;
}

// Frontend equivalent of backend canActivateUser
export function canActivateTargetUser(currentUserPermissions, targetUser) {
  if (!currentUserPermissions || !targetUser) return false;
  // Use the frontend canManageTargetUser first
  if (!canManageTargetUser(currentUserPermissions, targetUser)) return false;

  // Check activation pending (Only OW can activate pending users)
  const requestingUser = { 
    primaryRole: currentUserPermissions.primaryRole,
    secondaryRoles: currentUserPermissions.secondaryRoles
  };
  const requesterRoleCategory = getRoleCategory(requestingUser);
  if (targetUser.activationPending && requesterRoleCategory !== 'OW') {
      //  // Removed log
      return false;
  }

  // Organization Level Check - Org Level 2 cannot reactivate if deactivated by Org Level 1
  const requesterOrgLevel = getUserOrganizationLevel(requestingUser);
  if (targetUser.statusLastModifiedByOrgLevel === 1 && requesterOrgLevel === 2) {
      //  // Removed log
      return false;
  }

  // Legacy check for backward compatibility
  if (targetUser.deactivatedByRoleCategory === 'OW' && requesterRoleCategory !== 'OW') {
      //  // Removed log
      return false;
  }

  // If we reach here, activation is allowed based on hierarchy and organization level
  return true;
}

// Frontend equivalent of backend canDeactivateUser
export function canDeactivateTargetUser(currentUserPermissions, targetUser) {
  if (!currentUserPermissions || !targetUser) return false;
  // Use the frontend canManageTargetUser
  return canManageTargetUser(currentUserPermissions, targetUser);
}

// Frontend equivalent of backend canEditTargetUserRoles
export function canEditTargetUserRoles(currentUserPermissions, targetUser) {
    if (!currentUserPermissions || !targetUser) {
        // console.warn("canEditTargetUserRoles (FE): Missing args"); // Removed log
        return false;
    }
    const requesterId = currentUserPermissions.userId;
    const targetId = targetUser._id || targetUser.id;

    if (requesterId === targetId) {
        //  Denied: Cannot edit own roles.");
        return false; // Cannot edit self
    }

    // Reconstruct a simplified requestingUser object for permission checks
    const requestingUser = {
        _id: requesterId,
        primaryRole: currentUserPermissions.primaryRole,
        secondaryRoles: currentUserPermissions.secondaryRoles,
        vendorNumber: currentUserPermissions.vendorId, // Use vendorId from permissions
        vendorId: currentUserPermissions.vendorId,
        regions: currentUserPermissions.regions || []
    };
    const targetPrimaryRole = targetUser.primaryRole || targetUser.role;
    const requesterPrimaryRole = requestingUser.primaryRole;
    const requesterVendorId = requestingUser.vendorNumber || requestingUser.vendorId;
    const targetVendorId = targetUser.vendorNumber || targetUser.vendorId;
    const isRequesterNAAdmin = requestingUser.secondaryRoles?.admin && requesterPrimaryRole === PRIMARY_ROLES.UNDEFINED;

    // OW/sOW can edit roles of anyone below them
    if (requesterPrimaryRole === PRIMARY_ROLES.OWNER || requesterPrimaryRole === PRIMARY_ROLES.SUB_OWNER) {
        const requesterLevel = getUserPrivilegeLevel(requestingUser);
        const targetLevel = getUserPrivilegeLevel(targetUser);
        return targetLevel < requesterLevel;
    }

    // RM can edit roles of users below them within their region
    if (requesterPrimaryRole === PRIMARY_ROLES.REGIONAL_MANAGER) {
        if (targetPrimaryRole === PRIMARY_ROLES.OWNER || targetPrimaryRole === PRIMARY_ROLES.SUB_OWNER || targetPrimaryRole === PRIMARY_ROLES.REGIONAL_MANAGER) return false;
        const targetRegions = targetUser.regions?.map(r => r._id || r.toString()) || [];
        const requesterRegions = requestingUser.regions || [];
        if (!Array.isArray(targetRegions) || !Array.isArray(requesterRegions) || requesterRegions.length === 0) return false;
        if (!targetRegions.some(tr => requesterRegions.includes(tr))) return false;
        return true;
    }

    // SP (including SP Admin) can edit roles of users (SP or N/A) within the same vendor, excluding higher roles
    if (requesterPrimaryRole === PRIMARY_ROLES.SERVICE_PROVIDER) {
        if (targetPrimaryRole === PRIMARY_ROLES.OWNER || targetPrimaryRole === PRIMARY_ROLES.SUB_OWNER || targetPrimaryRole === PRIMARY_ROLES.REGIONAL_MANAGER) return false;
        if (!requesterVendorId || !targetVendorId || requesterVendorId !== targetVendorId) return false;
        if (targetPrimaryRole === PRIMARY_ROLES.SERVICE_PROVIDER) {
             const isRequesterSPAdmin = requestingUser.secondaryRoles?.admin;
             const isTargetSPAdmin = targetUser.secondaryRoles?.admin;
             if (!isRequesterSPAdmin || isTargetSPAdmin) return false; // SP Admin can edit non-Admin SP, but not other Admins or self
        }
        return true;
    }

    // N/A Admin can edit roles of N/A users within the same vendor
    if (isRequesterNAAdmin) {
         if (targetPrimaryRole !== PRIMARY_ROLES.UNDEFINED) return false;
         if (!requesterVendorId || !targetVendorId || requesterVendorId !== targetVendorId) return false;
         return true;
    }

    // Default deny
    //  Denied: Default deny for ${requesterPrimaryRole} ${requesterId} editing roles for ${targetId}`);
    return false;
}


export function hasPermission(permission) {
  const storedPermissions = getStoredPermissions();
  if (!storedPermissions || !storedPermissions.isAuthenticated) return false;
  const { permissions } = storedPermissions;
  return !!(permissions && permissions[permission]);
}

export function getAllowedStatusTransitions(currentStatus) {
  const storedPermissions = getStoredPermissions();
  if (!storedPermissions || !storedPermissions.isAuthenticated) return [];
  const { permissions } = storedPermissions;
  return permissions?.allowedJobProgressions?.[currentStatus] || [];
}

export function canManageGoaRequests() {
  const storedPermissions = getStoredPermissions();
  if (!storedPermissions || !storedPermissions.isAuthenticated) return false;
  return storedPermissions.permissions?.canApproveGOA === true;
}

export function canManageUnsuccessfulRequests() {
  const storedPermissions = getStoredPermissions();
  if (!storedPermissions || !storedPermissions.isAuthenticated) return false;
  return storedPermissions.permissions?.canApproveUnsuccessful === true;
}

export function canDispatchJobs() {
  const storedPermissions = getStoredPermissions();
  if (!storedPermissions || !storedPermissions.isAuthenticated) return false;
  return storedPermissions.permissions?.canDispatchJobs === true;
}

export function canAccessPage(pageName) {
  if (pageName === 'dashboard' || pageName === 'Dashboard') return true;
  
  // Allow access to insurance-submission for users with admin secondary role or OW users
  if (pageName === 'insurance-submission') {
    const storedPermissions = getStoredPermissions();
    if (!storedPermissions || !storedPermissions.isAuthenticated) return false;
    const { permissions } = storedPermissions;
    // Allow users with admin secondary role, or OW/sOW users to access insurance submission page
    return permissions.secondaryRoles?.admin === true ||
           permissions.primaryRole === PRIMARY_ROLES.OWNER ||
           permissions.primaryRole === PRIMARY_ROLES.SUB_OWNER;
  }
  
  const storedPermissions = getStoredPermissions();
  if (!storedPermissions || !storedPermissions.isAuthenticated) return false;
  const { permissions } = storedPermissions;
  
  // Debug log to see what's happening
  
  
  if (!permissions?.pageAccess) return false;
  if (permissions.pageAccess[pageName] === true) return true;
  const pageAccessKeys = Object.keys(permissions.pageAccess);
  const matchingKey = pageAccessKeys.find(key => key.toLowerCase() === pageName.toLowerCase());
  return !!(matchingKey && permissions.pageAccess[matchingKey] === true);
}

export function canViewJob(job) {
  const storedPermissions = getStoredPermissions();
  if (!storedPermissions || !storedPermissions.isAuthenticated || !job) return false;
  const { permissions } = storedPermissions;
  if ([PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER].includes(permissions.primaryRole)) return true;
  if (job.visibleTo && Array.isArray(job.visibleTo)) return job.visibleTo.includes(permissions.userId);
  if (permissions.primaryRole === PRIMARY_ROLES.REGIONAL_MANAGER) {
      if (job.region && Array.isArray(permissions.regions)) {
          const jobRegionId = typeof job.region === 'object' ? job.region._id?.toString() : job.region?.toString();
          return permissions.regions.some(userRegion => userRegion?.toString() === jobRegionId);
      }
  }
  if (permissions.primaryRole === PRIMARY_ROLES.SERVICE_PROVIDER || (permissions.primaryRole === PRIMARY_ROLES.UNDEFINED && (permissions.secondaryRoles?.dispatcher || permissions.secondaryRoles?.admin || permissions.secondaryRoles?.answeringService))) {
      if (permissions.vendorId && (job.vendorId || job.vendorNumber)) {
          return permissions.vendorId === (job.vendorId || job.vendorNumber);
      }
  }
  if (permissions.isDriverOnly) {
      const targetDriverId = job.driverId ? job.driverId.toString() : null;
      return (targetDriverId && targetDriverId === permissions.userId) || (job.driver && job.driver === permissions.fullName);
  }
  return false;
}

export function canEditJob(job) {
  const storedPermissions = getStoredPermissions();
  if (!storedPermissions || !storedPermissions.isAuthenticated || !job) return false;
  const { permissions } = storedPermissions;
  if ([PRIMARY_ROLES.OWNER, PRIMARY_ROLES.SUB_OWNER].includes(permissions.primaryRole)) return true;
  if (permissions.primaryRole === PRIMARY_ROLES.REGIONAL_MANAGER) {
      if (job.region && Array.isArray(permissions.regions)) {
          const jobRegionId = typeof job.region === 'object' ? job.region._id?.toString() : job.region?.toString();
          return permissions.regions.some(userRegion => userRegion?.toString() === jobRegionId);
      }
  }
   if (permissions.primaryRole === PRIMARY_ROLES.SERVICE_PROVIDER || (permissions.primaryRole === PRIMARY_ROLES.UNDEFINED && (permissions.secondaryRoles?.dispatcher || permissions.secondaryRoles?.admin || permissions.secondaryRoles?.answeringService))) {
      if (permissions.vendorId && (job.vendorId || job.vendorNumber)) {
          return permissions.vendorId === (job.vendorId || job.vendorNumber);
      }
  }
  if (permissions.isDriverOnly) return false;
  return false;
}

export function canChangeJobStatus(currentStatus, targetStatus) {
  const storedPermissions = getStoredPermissions();
  if (!storedPermissions || !storedPermissions.isAuthenticated) return false;
  const { permissions } = storedPermissions;
  const allowedProgressions = permissions?.allowedJobProgressions?.[currentStatus] || [];
  return allowedProgressions.includes(targetStatus);
}

export function initializePermissions() {
  
  try {
    let userDataString = localStorage.getItem('userInfo') || localStorage.getItem('user');
    if (!userDataString) {
      
      clearPermissions();
      return null;
    }
    const userData = JSON.parse(userDataString);
    
    if (!userData || (!userData.id && !userData._id)) {
      
      clearPermissions();
      return null;
    }
    const permissionsData = processPermissions(userData);
    storePermissions(permissionsData);
    return permissionsData;
  } catch (error) {
    console.error('Error initializing permissions:', error);
    clearPermissions();
    return null;
  }
}

// Export getUserOrganizationLevel as well
export { getUserOrganizationLevel };

// Default export combining functions
export default {
  processPermissions, storePermissions, getStoredPermissions, clearPermissions, initializePermissions,
  canAccessPage, canViewJob, canEditJob, canChangeJobStatus, canManageTargetUser,
  canActivateTargetUser, canDeactivateTargetUser, hasPermission, getAllowedStatusTransitions,
  canManageGoaRequests, canManageUnsuccessfulRequests, canDispatchJobs, isDriverOnly,
  canEditTargetUserRoles, getUserOrganizationLevel, // Add to default export
  PERMISSION_TYPES, PRIMARY_ROLES, SECONDARY_ROLES, PAGES, JOB_STATUS_SEQUENCE,
};
